import React, { useState, useEffect } from 'react';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = ({ onBack }) => {
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
    setScrollProgress(progress);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 p-4">
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #10b981;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #059669;
        }
        .scroll-shadow {
          background: linear-gradient(white 30%, rgba(255,255,255,0)),
                      linear-gradient(rgba(255,255,255,0), white 70%) 0 100%,
                      radial-gradient(farthest-side at 50% 0, rgba(0,0,0,.15), rgba(0,0,0,0)),
                      radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.15), rgba(0,0,0,0)) 0 100%;
          background-repeat: no-repeat;
          background-color: white;
          background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;
          background-attachment: local, local, scroll, scroll;
        }
      `}</style>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-2xl mb-6 p-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onBack}
              className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Privacy Policy</h1>
                <p className="text-gray-600">123zzz Application</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content - Professional Scrollable Container */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Fixed Header in Content */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Privacy Policy</h2>
                <p className="text-sm text-gray-600">123zzz Application - Complete Privacy Document</p>
              </div>
              <div className="text-xs text-gray-500 bg-white px-3 py-1 rounded-full">
                {scrollProgress < 95 ? 'Scroll to read all 13 sections' : 'Document completed ✓'}
              </div>
            </div>
            {/* Progress Bar */}
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-green-500 to-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${scrollProgress}%` }}
              ></div>
            </div>
          </div>
          
          {/* Scrollable Content Area */}
          <div 
            className="max-h-[70vh] overflow-y-auto custom-scrollbar scroll-shadow"
            onScroll={handleScroll}
          >
            <div className="p-8">
              <div className="prose prose-gray max-w-none">
                <div className="text-sm text-gray-500 mb-6 sticky top-0 bg-white/90 backdrop-blur-sm p-2 rounded-lg border border-gray-200 flex items-center justify-between">
                  <span>Last updated: January 2025</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    {Math.round(scrollProgress)}% read
                  </span>
                </div>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">1. INTRODUCTION AND DATA CONTROLLER</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                This Privacy Policy describes how 123zzz (Data Controller) collects, uses, stores, and protects your personal information when you use our mobile application for AI-generated bedtime stories. We are committed to protecting your privacy in accordance with Australian Privacy Principles, GDPR, COPPA, CCPA, and other applicable international privacy regulations.
              </p>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="font-semibold text-blue-800 mb-2">Data Controller: 123zzz Pty Ltd, Brisbane, Queensland, Australia</p>
                <p className="text-blue-700">Contact: privacy@123zzz.com | Response time: 48 hours maximum</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">2. INFORMATION WE COLLECT</h2>
              
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-lg">📊</span>
                  </span>
                  <h3 className="font-bold text-blue-800">Data Collection Overview</h3>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  We collect only the information necessary to provide our bedtime story service. 
                  Our data collection is minimal, purposeful, and designed with children's privacy as our top priority.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white border-2 border-indigo-200 rounded-xl p-6">
                    <h3 className="font-bold text-indigo-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-indigo-500 rounded-full"></span>
                      Account & Authentication
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li><strong>Email address:</strong> Account creation and communication</li>
                      <li><strong>Authentication tokens:</strong> Firebase secure login</li>
                      <li><strong>Google Sign-In:</strong> Optional authentication method</li>
                      <li><strong>Account preferences:</strong> Language and basic settings</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border-2 border-purple-200 rounded-xl p-6">
                    <h3 className="font-bold text-purple-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-purple-500 rounded-full"></span>
                      Story Personalization
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li><strong>Child's first name:</strong> Story personalization only</li>
                      <li><strong>Age range:</strong> 2-12 years for content appropriateness</li>
                      <li><strong>Interests:</strong> Animals, adventures, magic, etc.</li>
                      <li><strong>Language preference:</strong> English or French</li>
                    </ul>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white border-2 border-green-200 rounded-xl p-6">
                    <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-green-500 rounded-full"></span>
                      Subscription & Billing
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li><strong>Plan type:</strong> Free, On-demand, or Standard</li>
                      <li><strong>Usage counters:</strong> Stories generated/remaining</li>
                      <li><strong>Payment information:</strong> Processed securely by Stripe</li>
                      <li><strong>Billing history:</strong> Transaction records and receipts</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border-2 border-orange-200 rounded-xl p-6">
                    <h3 className="font-bold text-orange-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-orange-500 rounded-full"></span>
                      Technical & Usage
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li><strong>Device information:</strong> Type, OS, app version</li>
                      <li><strong>Usage patterns:</strong> Feature usage, session duration</li>
                      <li><strong>Generated content:</strong> Stories and images created</li>
                      <li><strong>Performance data:</strong> Error logs, loading times</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                  <h3 className="font-bold text-red-800 mb-4 flex items-center gap-2">
                    <span className="w-4 h-4 bg-red-500 rounded-full"></span>
                    Children's Data Protection (Under 13)
                  </h3>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-red-700 mb-3 font-semibold text-sm">Enhanced COPPA Compliance Protection:</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-red-700 font-semibold mb-2 text-sm">Minimal Collection:</p>
                        <ul className="list-disc list-inside text-red-700 space-y-1 text-xs ml-4">
                          <li>First name only (no last name)</li>
                          <li>Age range (not specific birthdate)</li>
                          <li>Basic story preferences</li>
                          <li>No location or contact information</li>
                        </ul>
                      </div>
                      <div>
                        <p className="text-red-700 font-semibold mb-2 text-sm">Special Protections:</p>
                        <ul className="list-disc list-inside text-red-700 space-y-1 text-xs ml-4">
                          <li>Verified parental consent required</li>
                          <li>No data sharing with third parties</li>
                          <li>No behavioral tracking or profiling</li>
                          <li>Automatic deletion at age 13</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">3. HOW WE USE YOUR INFORMATION</h2>
              
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-lg">🎯</span>
                  </span>
                  <h3 className="font-bold text-green-800">Purpose-Driven Data Usage</h3>
                </div>
                <p className="text-green-700 text-sm leading-relaxed">
                  Every piece of data we collect serves a specific purpose in delivering our bedtime story service. 
                  We never use your information for purposes beyond what you've consented to.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-white border-2 border-blue-200 rounded-xl p-6">
                    <h3 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-blue-500 rounded-full"></span>
                      Core Service Delivery
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li>Generate personalized AI bedtime stories</li>
                      <li>Create custom illustrations for each story</li>
                      <li>Provide 4-part narrative structure</li>
                      <li>Enable multi-language story generation</li>
                      <li>Save and sync stories across devices</li>
                      <li>Manage subscription and usage limits</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border-2 border-purple-200 rounded-xl p-6">
                    <h3 className="font-bold text-purple-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-purple-500 rounded-full"></span>
                      Account & Billing Management
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li>Process On-demand and Standard subscriptions</li>
                      <li>Track story usage and limits</li>
                      <li>Provide customer support</li>
                      <li>Send service notifications and updates</li>
                      <li>Manage account security and authentication</li>
                      <li>Handle billing and payment processing</li>
                    </ul>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-white border-2 border-green-200 rounded-xl p-6">
                    <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-green-500 rounded-full"></span>
                      Service Improvement
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li>Enhance AI story generation quality</li>
                      <li>Improve image generation accuracy</li>
                      <li>Optimize app performance and stability</li>
                      <li>Develop new story themes and interests</li>
                      <li>Analyze usage patterns for better UX</li>
                      <li>Fix bugs and technical issues</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border-2 border-orange-200 rounded-xl p-6">
                    <h3 className="font-bold text-orange-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-orange-500 rounded-full"></span>
                      Legal & Safety Compliance
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li>Meet COPPA, GDPR, and privacy regulations</li>
                      <li>Ensure child safety and content appropriateness</li>
                      <li>Respond to legal requests when required</li>
                      <li>Prevent fraud and service abuse</li>
                      <li>Maintain audit trails for compliance</li>
                      <li>Protect user rights and data security</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mt-6">
                <h3 className="font-bold text-yellow-800 mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 bg-yellow-200 rounded-full flex items-center justify-center">
                    <span className="text-yellow-700 text-xs">⚠️</span>
                  </span>
                  What We DON'T Do With Your Data
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 text-sm ml-4">
                    <li>No advertising or marketing targeting</li>
                    <li>No data selling to third parties</li>
                    <li>No behavioral profiling for children</li>
                    <li>No cross-platform tracking</li>
                  </ul>
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 text-sm ml-4">
                    <li>No sharing with data brokers</li>
                    <li>No AI training on children's personal data</li>
                    <li>No location tracking or geofencing</li>
                    <li>No social media integration or sharing</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">4. INFORMATION SHARING AND DISCLOSURE</h2>
              <div className="bg-red-50 rounded-xl p-6 mb-4">
                <p className="font-bold text-red-800 text-center">We NEVER sell, rent, or trade your personal information.</p>
              </div>
              <p className="text-gray-700 mb-4">Limited sharing occurs only in these specific cases:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Service Providers:</strong> Google Cloud (hosting), Firebase (authentication), Stripe (payments), with strict data processing agreements</li>
                <li><strong>AI Partners:</strong> Google Gemini and Imagen4 APIs for content generation (data anonymized)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect safety (with prior notice when legally permitted)</li>
                <li><strong>Business Transfers:</strong> In case of merger or acquisition, with equivalent privacy protections and user notification</li>
                <li><strong>Parental Access:</strong> Parents can access all their children's data and generation history</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">5. DATA SECURITY AND PROTECTION</h2>
              <p className="font-semibold text-gray-800 mb-4">Industry-Leading Security Measures:</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">Encryption</h3>
                    <p className="text-gray-700 text-sm">AES-256 encryption for all data transmission and storage</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Access Controls</h3>
                    <p className="text-gray-700 text-sm">Role-based access with multi-factor authentication</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Infrastructure</h3>
                    <p className="text-gray-700 text-sm">Google Cloud Platform with ISO 27001 certification</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">Monitoring</h3>
                    <p className="text-gray-700 text-sm">24/7 security monitoring and incident response</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Audits</h3>
                    <p className="text-gray-700 text-sm">Regular penetration testing and security assessments</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Backups</h3>
                    <p className="text-gray-700 text-sm">Encrypted backups with geographic redundancy</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">6. DATA RETENTION AND DELETION</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <span className="font-semibold text-gray-800">Account Data</span>
                  <span className="text-gray-700 text-sm">Retained while active + 30 days after deletion</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <span className="font-semibold text-gray-800">Generated Stories</span>
                  <span className="text-gray-700 text-sm">Until user deletion, permanent deletion within 30 days</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <span className="font-semibold text-gray-800">Subscription Data</span>
                  <span className="text-gray-700 text-sm">7 years for tax and accounting compliance</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-red-50 rounded-xl">
                  <span className="font-semibold text-red-800">Children's Data</span>
                  <span className="text-red-700 text-sm">Auto-deleted when child turns 13 or upon parental request</span>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">7. YOUR PRIVACY RIGHTS</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-bold text-blue-800 mb-3">Individual Rights</h3>
                  <ul className="list-disc list-inside text-blue-700 space-y-1 text-sm ml-4">
                    <li><strong>Access:</strong> Request copies of your data</li>
                    <li><strong>Rectification:</strong> Correct inaccurate information</li>
                    <li><strong>Erasure:</strong> Request data deletion</li>
                    <li><strong>Portability:</strong> Export your data</li>
                    <li><strong>Restriction:</strong> Limit data processing</li>
                    <li><strong>Objection:</strong> Opt-out of processing</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="font-bold text-green-800 mb-3">Parental Rights</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1 text-sm ml-4">
                    <li><strong>Review:</strong> Access all children's data</li>
                    <li><strong>Modify:</strong> Update child information</li>
                    <li><strong>Delete:</strong> Remove child's account</li>
                    <li><strong>Control:</strong> Manage data sharing</li>
                    <li><strong>Monitor:</strong> View usage history</li>
                    <li><strong>Consent:</strong> Withdraw consent anytime</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">8. INTERNATIONAL DATA TRANSFERS</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your data may be processed in Australia, United States (Google Cloud), and Ireland (EU data center) where our service providers operate. We ensure adequate protection through:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Standard Contractual Clauses:</strong> GDPR-approved data transfer mechanisms</li>
                <li><strong>Adequacy Decisions:</strong> Transfers only to countries with adequate data protection</li>
                <li><strong>Privacy Shield Replacements:</strong> Latest EU-US data transfer frameworks</li>
                <li><strong>Local Laws:</strong> Compliance with destination country privacy laws</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">9. COOKIES AND TRACKING TECHNOLOGIES</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">What Are Cookies and Similar Technologies</h3>
                  <p className="text-gray-700 mb-4">
                    Cookies are small text files stored on your device when you use our application. We also use local storage, pixels, and SDKs for enhanced functionality and analytics.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-green-50 rounded-xl p-6">
                    <h3 className="font-bold text-green-800 mb-3">Essential Cookies (Always Active)</h3>
                    <ul className="list-disc list-inside text-green-700 space-y-1 text-sm ml-4">
                      <li>Authentication: Keep you logged in securely</li>
                      <li>Functionality: Remember language preferences and app settings</li>
                      <li>Security: Prevent fraud and protect against threats</li>
                      <li>Performance: Ensure app stability and proper functioning</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-6">
                    <h3 className="font-bold text-blue-800 mb-3">Analytical Cookies (Optional)</h3>
                    <ul className="list-disc list-inside text-blue-700 space-y-1 text-sm ml-4">
                      <li>Usage Analytics: Understand feature interaction (anonymized)</li>
                      <li>Performance Monitoring: Track app crashes and loading times</li>
                      <li>Feature Usage: Identify popular content for improvements</li>
                      <li>Error Reporting: Diagnose and fix technical issues</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6">
                  <h3 className="font-bold text-yellow-800 mb-3">Children's Cookie Protection</h3>
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 ml-4">
                    <li>No advertising or tracking cookies for users under 13</li>
                    <li>Only essential cookies for functionality</li>
                    <li>No behavioral profiling or targeting</li>
                    <li>Parents can review and control all cookie settings</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">10. THIRD-PARTY SERVICES</h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-600 text-lg">🔗</span>
                  </span>
                  <h3 className="font-bold text-gray-800">Trusted Service Partners</h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  We work with carefully selected, industry-leading service providers to deliver our bedtime story service. 
                  All partners are bound by strict data processing agreements.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border-2 border-blue-200 rounded-xl p-6">
                  <h3 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-sm">🔐</span>
                    </span>
                    Firebase/Google Cloud
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-blue-700 font-semibold text-sm mb-1">Services Used:</p>
                      <ul className="list-disc list-inside text-gray-700 space-y-1 text-xs ml-4">
                        <li>User authentication and account management</li>
                        <li>Secure cloud storage for stories and preferences</li>
                        <li>AI-powered story and image generation</li>
                        <li>Real-time database synchronization</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-blue-700 font-semibold text-sm mb-1">Privacy Policy:</p>
                      <p className="text-xs text-blue-600">policies.google.com/privacy</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border-2 border-green-200 rounded-xl p-6">
                  <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-sm">💳</span>
                    </span>
                    Stripe Payments
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-green-700 font-semibold text-sm mb-1">Services Used:</p>
                      <ul className="list-disc list-inside text-gray-700 space-y-1 text-xs ml-4">
                        <li>Secure payment processing (On-demand & Standard)</li>
                        <li>Subscription management and billing</li>
                        <li>PCI DSS compliant card handling</li>
                        <li>Fraud detection and prevention</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-green-700 font-semibold text-sm mb-1">Privacy Policy:</p>
                      <p className="text-xs text-green-600">stripe.com/privacy</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 bg-indigo-50 rounded-xl p-6">
                <h3 className="font-bold text-indigo-800 mb-3">Data Processing Agreements</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-indigo-700 font-semibold text-sm mb-2">Security Requirements:</p>
                    <ul className="list-disc list-inside text-indigo-700 space-y-1 text-xs ml-4">
                      <li>Industry-standard encryption (AES-256)</li>
                      <li>Regular security audits and compliance checks</li>
                      <li>Restricted access on need-to-know basis</li>
                      <li>Immediate breach notification protocols</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-indigo-700 font-semibold text-sm mb-2">Usage Limitations:</p>
                    <ul className="list-disc list-inside text-indigo-700 space-y-1 text-xs ml-4">
                      <li>Data used only for specified services</li>
                      <li>No sharing with other third parties</li>
                      <li>Deletion upon contract termination</li>
                      <li>Full GDPR and COPPA compliance</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">11. POLICY UPDATES AND NOTIFICATIONS</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>We may update this policy with 30 days' notice via app notification or email</li>
                <li>Material changes affecting children's data require renewed parental consent</li>
                <li>Continued use after changes constitutes acceptance of the updated policy</li>
                <li>Users can review change history and previous policy versions upon request</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">12. CONTACT INFORMATION</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Privacy Questions:</h3>
                    <p className="text-gray-700 ml-4">privacy@123zzz.com</p>
                    <p className="text-gray-700 ml-4 text-sm">Response within 48 hours</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Data Protection Officer:</h3>
                    <p className="text-gray-700 ml-4">dpo@123zzz.com</p>
                    <p className="text-gray-700 ml-4 text-sm">Available for EU residents</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Customer Support:</h3>
                    <p className="text-gray-700 ml-4">24/7 in-app chat assistant</p>
                    <p className="text-gray-700 ml-4">support@123zzz.com</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Postal Address:</h3>
                    <p className="text-gray-700 ml-4">123zzz Privacy Officer</p>
                    <p className="text-gray-700 ml-4">Brisbane, Queensland, Australia</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">13. REGULATORY COMPLAINTS</h2>
              <p className="text-gray-700 mb-4">If unsatisfied with our response to your privacy concerns, you may contact:</p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-yellow-50 rounded-xl p-6">
                  <h3 className="font-bold text-yellow-800 mb-2">Australia</h3>
                  <p className="text-yellow-700 text-sm">Office of the Australian Information Commissioner (OAIC)</p>
                  <p className="text-yellow-700 text-sm">oaic.gov.au</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-bold text-blue-800 mb-2">European Union</h3>
                  <p className="text-blue-700 text-sm">Your local Data Protection Authority</p>
                  <p className="text-blue-700 text-sm">edpb.europa.eu</p>
                </div>
                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="font-bold text-red-800 mb-2">United States</h3>
                  <p className="text-red-700 text-sm">Federal Trade Commission (FTC)</p>
                  <p className="text-red-700 text-sm">ftc.gov/coppa</p>
                </div>
              </div>
            </section>

            <div className="mt-12 p-6 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600 text-center">
                <strong>Effective Date:</strong> This Privacy Policy is effective immediately upon acceptance and supersedes all previous versions. 
                We continuously update our practices to ensure the highest level of privacy protection for our users and their families.
              </p>
            </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;